import { Stack } from '@chakra-ui/react';
import {
  ChartPieIcon,
  CurrencyEuroIcon,
  FolderIcon,
  HomeIcon,
  OfficeBuildingIcon,
} from '@heroicons/react/outline';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { routes } from '@blockpulse3/data/shared';
import { CompanyStatus, useGetCompanyQuery } from '@blockpulse3/graphql/hooks';
import { NavGroupItem } from '@blockpulse3/ui/commons';

type Props = unknown;

export function SettingsNavCompany(): JSX.Element {
  const i18nSidebar = useTranslations('Sidebar');

  const { pathname } = useLocation();
  const { companyId = '' } = useParams();
  const companyReq = useGetCompanyQuery({
    variables: { companyId },
    skip: !companyId,
  });
  const company = companyReq?.data?.company;

  const hasCaptable = !!company?.capTable;

  const homeRoute = routes.workspace.fmt(companyId);
  const captableRoute = routes.workspace.captable.fmt(companyId);
  const documentsRoute = routes.workspace.documents.fmt(companyId);
  const settingsRoute = routes.workspace.settings.fmt(companyId);

  /* ** Assume workspace is complete when creation date is set ** */
  const isComplete = company?.status === CompanyStatus.RUNNING;

  return (
    <Stack spacing="1">
      {isComplete ? (
        <>
          <NavGroupItem
            active={pathname === homeRoute}
            href={homeRoute}
            icon={<HomeIcon height="18" width="18" />}
            label={i18nSidebar('HomeTitle')}
          />
          {hasCaptable && (
            <NavGroupItem
              active={pathname === captableRoute}
              href={captableRoute}
              icon={<ChartPieIcon height="18" width="18" />}
              label={i18nSidebar('CaptableTitle')}
            />
          )}
          <NavGroupItem
            active={pathname === documentsRoute}
            href={documentsRoute}
            icon={<FolderIcon height="18" width="18" />}
            label={i18nSidebar('DocumentsTitle')}
          />
          {company?.bankAccount?.bankDashboardUrl && (
            <NavGroupItem
              href={company?.bankAccount?.bankDashboardUrl}
              icon={<CurrencyEuroIcon height="18" width="18" />}
              label={i18nSidebar('AccountTitle')}
            />
          )}
        </>
      ) : null}
      <NavGroupItem
        active={pathname === settingsRoute}
        href={settingsRoute}
        icon={<OfficeBuildingIcon height="18" width="18" />}
        label={i18nSidebar('SettingsTitle')}
      />
    </Stack>
  );
}

export type SettingsNavCompanyProps = Props;
